import React, { useState } from 'react';
import { FiMail } from 'react-icons/fi';
import { Link, useNavigate } from '@tanstack/react-router';
import { toast } from '../../utils/toast';
import { useAuth } from '../../hooks/useAuth';
import { mapUserFromAuthResponse } from '../../contexts/AuthContext';
import { GoogleLogin, type CredentialResponse } from '@react-oauth/google';
import { useSendOtpMutation, useGoogleLoginMutation } from '../../api/auth';
import { useAppleAuth } from '../../hooks/useAppleAuth';
import { signupStore } from '../../utils/signupStore';
import { locationHistoryService } from '../../services/locationHistoryService';
import { extractAuthSession } from '../../utils/authSession';
import { extractApiError, extractFieldErrors, hasErrorCode } from '../../utils/apiError';
import { AuthHeader } from './AuthHeader';

const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

type ApiUser = {
  id?: string | number;
  email?: string;
  username?: string;
  full_name?: string;
  fullName?: string;
  city?: string;
  state?: string;
  country?: string;
  genres?: string[];
  tags?: string[];
  user_types?: unknown[];
  prof_pics?: string;
  profPics?: string;
  profile_incomplete?: boolean;
  needs_profile_update?: boolean;
  role?: 'creator' | 'fan' | 'venue' | null;
};

// Removed local mapUserFromAuthResponse to use the shared one from AuthContext

export const SignUpPage: React.FC = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const sendOtpMutation = useSendOtpMutation();
  const googleMutation = useGoogleLoginMutation();
  const { handleAppleSignIn, isAppleLoading } = useAppleAuth();

  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [emailTaken, setEmailTaken] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Clear signup draft and location history whenever someone starts fresh from this page
  React.useEffect(() => {
    signupStore.clear();
    locationHistoryService.clearHistory();
  }, []);

  const handleContinue = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = email.trim().toLowerCase();

    if (!trimmed) {
      setError('Email is required.');
      return;
    }
    if (!emailRegex.test(trimmed)) {
      setError('Enter a valid email address.');
      return;
    }

    setError('');
    setEmailTaken(false);
    setIsLoading(true);
    try {
      await sendOtpMutation.mutateAsync({ email: trimmed, type: 'registration' });
      // Persist email and mark signup flow active with creator as the default account type
      signupStore.set({ flowActive: true, email: trimmed, role: 'creator' });
      navigate({ to: '/verify-email', search: { email: trimmed } });
    } catch (err: any) {
      const msg = extractApiError(err, 'Failed to send verification code. Please try again.');
      setError(extractFieldErrors(err).email ?? msg);
      setEmailTaken(hasErrorCode(err, 'EMAIL_ALREADY_REGISTERED'));
      // Removed redundant toast.error as mutation's onError handles it
    } finally {
      setIsLoading(false);
    }
  };

  const onGoogleSuccess = async (credentialResponse: CredentialResponse) => {
    if (!credentialResponse.credential) return;

    setIsLoading(true);
    try {
      const data = await googleMutation.mutateAsync(credentialResponse.credential);

      const { rawUser, accessToken, refreshToken } = extractAuthSession(data);

      if (accessToken) {
        const mappedUser = mapUserFromAuthResponse((rawUser ?? {}) as ApiUser);
        login(accessToken, mappedUser, refreshToken ?? undefined);

        if (mappedUser.profileIncomplete) {
          toast.info('Complete your profile details to unlock all features.');
        }

        navigate({ to: '/' });
      } else if (rawUser?.email) {
        signupStore.set({
          flowActive: true,
          email: rawUser.email,
          role: 'creator',
          fullName: rawUser.first_name || rawUser.full_name || '',
          profilePicUrl: rawUser.prof_pics || rawUser.google_prof_pics || '',
          googleSignedUp: true,
        });
        toast.info('Google account verified. Please complete your registration.');
        navigate({ to: '/personal-information' });
      }
    } catch (error) {
      console.error('Google login failed:', error);
      // Removed redundant toast.error as mutation's onError handles it
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="relative min-h-screen w-full flex bg-[#030303] flex-col overflow-hidden font-['Inter'] selection:bg-blue-500/30">
      {/* Background glow */}
      <div
        className="absolute pointer-events-none rounded-full"
        style={{
          width: '1308px',
          height: '449px',
          bottom: '-150px',
          left: '50%',
          transform: 'translateX(-50%)',
          background: 'linear-gradient(to right, #2F80ED, #942FED)',
          opacity: 0.5,
          filter: 'blur(260px)',
          zIndex: 0,
        }}
      />

      <AuthHeader />

      {/* Main content */}
      <div className="relative z-10 flex-1 flex flex-col items-center justify-center w-full max-w-lg mx-auto px-5 sm:px-6">
        <div className="w-full flex flex-col items-center text-center">
          <h1
            className="text-white text-[26px] sm:text-[32px] md:text-[40px] font-medium leading-[1.1] mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Complete Account
          </h1>
          <h2
            className="text-white text-[20px] sm:text-[22px] font-semibold mb-2"
            style={{ fontFamily: "'Funnel Display', sans-serif" }}
          >
            Verify your Email
          </h2>
          <p className="text-[#a1a1aa] text-[14px] md:text-[15px] mb-8 font-medium">
            Enter your email address to verify it.
          </p>

          <form className="w-full flex flex-col gap-4" onSubmit={handleContinue}>
            {/* Email input */}
            <div className="flex flex-col gap-1 text-left">
              <div
                className={`flex items-center bg-[#0a0a0a] border rounded-xl h-12 px-4 transition-colors ${
                  error ? 'border-red-500/50' : 'border-white/5 focus-within:border-blue-500/50'
                }`}
              >
                <FiMail className="text-zinc-500 mr-3 shrink-0" size={18} />
                <input
                  type="email"
                  value={email}
                  onChange={(e) => {
                    setEmail(e.target.value);
                    if (error) setError('');
                  }}
                  placeholder="E.g. grahama@gmail.com"
                  className="bg-transparent border-none text-white text-[14px] placeholder:text-zinc-600 w-full focus:outline-none"
                  autoComplete="email"
                />
              </div>
              {error ? <p className="text-red-400 text-xs">{error}</p> : null}
              {emailTaken ? (
                <Link
                  to="/sign-in"
                  search={{ email: email.trim().toLowerCase() }}
                  className="text-[#3B82F6] hover:text-blue-400 text-xs font-medium"
                >
                  Sign in instead
                </Link>
              ) : null}
            </div>

            <button
              type="submit"
              disabled={isLoading}
              className="h-12 w-full bg-white hover:bg-zinc-100 text-black font-semibold text-[15px] rounded-xl transition-all shadow-lg active:scale-[0.98] cursor-pointer disabled:opacity-60 disabled:cursor-not-allowed mt-2"
            >
              {isLoading ? 'Sending code…' : 'Continue'}
            </button>
          </form>

          {/* Social Divider */}
          <div className="flex items-center gap-4 my-8 w-full">
            <div className="h-px bg-white/10 flex-1"></div>
            <span className="text-zinc-500 text-[13px]">or</span>
            <div className="h-px bg-white/10 flex-1"></div>
          </div>

          {/* Social Logins */}
          <div className="w-full flex flex-col gap-3">
            {/* Apple Sign-In */}
            <button
              type="button"
              onClick={handleAppleSignIn}
              disabled={isLoading || isAppleLoading}
              className="flex items-center justify-center gap-3 w-full h-12 bg-transparent hover:bg-white/5 border border-white/10 rounded-xl text-white font-medium text-[14px] transition-colors disabled:opacity-50 cursor-pointer"
            >
              {isAppleLoading ? (
                <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" />
              ) : (
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="white"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path d="M17.05 20.28c-.98.95-2.05.8-3.08.35-1.09-.46-2.09-.48-3.24 0-1.44.62-2.2.44-3.06-.35C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.19 2.24-.86 3.43-.88 1.5-.02 2.73.59 3.44 1.59-3.41 1.95-2.88 6.42.34 7.68-.78 1.52-1.74 3.12-2.29 3.78zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                </svg>
              )}
              Continue with Apple
            </button>

            <div className="relative group">
              <button
                disabled={isLoading}
                className="flex items-center justify-center gap-3 w-full h-12 bg-transparent hover:bg-white/5 border border-white/10 rounded-xl text-white font-medium text-[14px] transition-colors disabled:opacity-50 cursor-pointer"
              >
                <svg
                  width="20"
                  height="20"
                  viewBox="0 0 24 24"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <path
                    d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
                    fill="#4285F4"
                  />
                  <path
                    d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.16v2.84C3.99 20.53 7.7 23 12 23z"
                    fill="#34A853"
                  />
                  <path
                    d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.16C1.43 8.55 1 10.22 1 12s.43 3.45 1.16 4.93l2.48-1.92.12-.92z"
                    fill="#FBBC05"
                  />
                  <path
                    d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.16 7.07l3.68 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
                    fill="#EA4335"
                  />
                </svg>
                Continue with Google
              </button>
              <div className="absolute inset-0 opacity-0 cursor-pointer overflow-hidden flex justify-center scale-[1.5]">
                <GoogleLogin
                  onSuccess={onGoogleSuccess}
                  onError={() => console.error('Google Login Error')}
                  useOneTap
                  theme="filled_black"
                  shape="rectangular"
                  width="400"
                />
              </div>
            </div>
          </div>

          <p className="mt-6 text-[14px] text-[#a1a1aa]">
            Already have an account?{' '}
            <Link to="/sign-in" className="text-white font-semibold hover:underline">
              Sign In
            </Link>
          </p>
        </div>
      </div>
    </div>
  );
};
